// src/pages/organiser/EditEventPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getEvent, updateEvent, buildRRule } from '../../lib/db';
import { storage } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../lib/AuthContext';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { value: 'music',     label: 'Music',     emoji: '🎵' },
  { value: 'sport_gaa', label: 'Sport - GAA',           emoji: '🏐' },
  { value: 'sport',     label: 'Sport - Other',         emoji: '🏃' },
  { value: 'kids',      label: 'Kids and Family',       emoji: '🧒' },
  { value: 'health',    label: 'Health and Wellbeing',  emoji: '🏥' },
  { value: 'farming',   label: 'Farming and Equine',    emoji: '🌾' },
  { value: 'food',      label: 'Food and Drink',        emoji: '🍽' },
  { value: 'education', label: 'Education',             emoji: '📚' },
  { value: 'social',    label: 'Social',                emoji: '🎉' },
  { value: 'community', label: 'Community', emoji: '🤝' },
  { value: 'markets',   label: 'Markets',   emoji: '🛒' },
  { value: 'arts',      label: 'Arts',      emoji: '🎭' },
];

const REPEATS = [
  { value: 'once',        label: 'One-off',     emoji: '1️⃣' },
  { value: 'weekly',      label: 'Weekly',      emoji: '🔁' },
  { value: 'fortnightly', label: 'Fortnightly', emoji: '📅' },
  { value: 'monthly',     label: 'Monthly',     emoji: '🗓️' },
];

const EditEventPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isOrganiser } = useAuth();
  const [form, setForm] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getEvent(id).then(ev => {
      if (ev) {
        const d = ev.date?.toDate ? ev.date.toDate() : new Date(ev.date);
        // Format date as yyyy-mm-dd for native date picker
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        // Format time as HH:mm for native time picker
        const timeStr = ev.time || '';
        let formattedTime = timeStr;
        // If stored as "8:00pm" format, convert to HH:mm
        const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})(am|pm)?$/i);
        if (timeMatch) {
          let h = parseInt(timeMatch[1]);
          const m = timeMatch[2];
          const ap = (timeMatch[3] || '').toLowerCase();
          if (ap === 'pm' && h !== 12) h += 12;
          if (ap === 'am' && h === 12) h = 0;
          formattedTime = `${String(h).padStart(2, '0')}:${m}`;
        }

        setForm({
          title:          ev.title          || '',
          description:    ev.description    || '',
          date:           `${yyyy}-${mm}-${dd}`,
          time:           formattedTime,
          venue:          ev.venue          || '',
          category:       ev.category       || 'music',
          eventType:      ev.eventType === 'interested' || ev.eventType === 'register' ? 'free' : (ev.eventType || 'free'),
          price:          ev.eventCharge ? String(ev.eventCharge).replace(/[^0-9.]/g, '') : '',
          repeat:         ev.repeat         || 'once',
          organiserPhone: ev.organiserPhone || '',
          organiserEmail: ev.organiserEmail || '',
          bookingUrl:     ev.bookingUrl     || '',
          imageUrl:       ev.imageUrl       || null,
        });
      }
    });
  }, [id]);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(e => ({ ...e, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim())       e.title = 'Event name is required';
    if (!form.date)               e.date = 'Date is required';
    if (!form.time)               e.time = 'Time is required';
    if (!form.venue.trim())       e.venue = 'Venue is required';
    if (!form.description.trim()) e.description = 'Description is required';
    if (form.eventType === 'paid' && !form.price) e.price = 'Price is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) { toast.error('Please fill in all required fields'); return; }
    setSaving(true);
    try {
      const [year, month, day] = form.date.split('-');
      const [hours, minutes] = form.time.split(':').map(Number);
      const eventDate = new Date(
        parseInt(year), parseInt(month) - 1, parseInt(day), hours || 0, minutes || 0
      );

      let imageUrl = form.imageUrl;
      if (imageFile) {
        const imgRef = ref(storage, `events/${user.uid}/${Date.now()}`);
        await uploadBytes(imgRef, imageFile);
        imageUrl = await getDownloadURL(imgRef);
      }

      await updateEvent(id, {
        title:          form.title.trim(),
        description:    form.description.trim(),
        date:           eventDate,
        nextDate:       eventDate,
        time:           form.time,
        venue:          form.venue.trim(),
        category:       form.category,
        eventType:      form.eventType,
        eventCharge:    form.eventType === 'paid' ? `€${form.price}` : 'Free',
        repeat:         form.repeat,
        rrule:          buildRRule(eventDate, form.repeat),
        organiserPhone: form.organiserPhone || null,
        organiserEmail: form.organiserEmail || null,
        bookingUrl:     form.bookingUrl     || null,
        imageUrl,
      });

      toast.success('Event updated!');
      // A Town Admin editing an event isn't an organiser — they have no
      // /organiser dashboard to land on, and would just get bounced back
      // out again by RequireOrganiser (the exact bug this fixes).
      navigate(isOrganiser ? '/organiser' : '/admin/events');
    } catch (err) {
      console.error(err);
      toast.error('Update failed. Try again.');
    }
    setSaving(false);
  };

  if (!form) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div className="spinner" />
    </div>
  );

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', paddingBottom: 110 }}>

      {/* Header */}
      <div style={{ padding: '52px 20px 16px' }}>
        <button onClick={() => navigate(isOrganiser ? '/organiser' : '/admin/events')} style={s.back}>
          <div style={s.backCircle}>←</div>
          <span style={s.backLbl}>My events</span>
        </button>
        <div style={s.h2}>Edit event</div>
      </div>

      <div style={s.form}>

        {/* Event name */}
        <Field label="Event name *" error={errors.title}>
          <input
            style={{ ...s.input, borderColor: errors.title ? 'var(--error)' : '#C8D9C9' }}
            value={form.title}
            onChange={e => set('title', e.target.value)}
          />
        </Field>

        {/* Description */}
        <Field label="Description *" error={errors.description}>
          <textarea
            style={{ ...s.input, height: 90, resize: 'none', borderColor: errors.description ? 'var(--error)' : '#C8D9C9' }}
            value={form.description}
            onChange={e => set('description', e.target.value)}
          />
        </Field>

        {/* Date & Time */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Date *" error={errors.date}>
            <input
              type="date"
              style={{ ...s.input, borderColor: errors.date ? 'var(--error)' : '#C8D9C9', colorScheme: 'light' }}
              value={form.date}
              onChange={e => set('date', e.target.value)}
            />
          </Field>
          <Field label="Time *" error={errors.time}>
            <input
              type="time"
              step="900"
              style={{ ...s.input, borderColor: errors.time ? 'var(--error)' : '#C8D9C9', colorScheme: 'light' }}
              value={form.time}
              onChange={e => set('time', e.target.value)}
            />
          </Field>
        </div>

        {/* Venue */}
        <Field label="Location / Venue *" error={errors.venue}>
          <input
            style={{ ...s.input, borderColor: errors.venue ? 'var(--error)' : '#C8D9C9' }}
            value={form.venue}
            onChange={e => set('venue', e.target.value)}
          />
        </Field>

        {/* Category */}
        <Field label="Category">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
            {CATEGORIES.map(cat => (
              <button
                key={cat.value}
                style={{
                  ...s.tileBtn,
                  border: form.category === cat.value ? '2px solid var(--sage-deep)' : '1.5px solid #C8D9C9',
                  background: form.category === cat.value ? 'var(--sage-pale)' : 'var(--white)',
                  color: form.category === cat.value ? 'var(--sage-deep)' : '#2C4A2E',
                  fontWeight: form.category === cat.value ? 800 : 600,
                }}
                onClick={() => set('category', cat.value)}
              >
                <span style={{ fontSize: 20, display: 'block', marginBottom: 4 }}>{cat.emoji}</span>
                {cat.label}
              </button>
            ))}
          </div>
        </Field>

        {/* Free / Paid */}
        <Field label="Is this event free or paid?">
          <div style={{ display: 'flex', gap: 10 }}>
            {[
              { value: 'free', label: 'Free', emoji: '🎉', sub: 'No charge to attend' },
              { value: 'paid', label: 'Paid', emoji: '💶', sub: 'Set a price' },
            ].map(et => (
              <button
                key={et.value}
                style={{
                  ...s.etypeBtn,
                  flex: 1,
                  border: form.eventType === et.value ? '2px solid var(--sage-deep)' : '1.5px solid #C8D9C9',
                  background: form.eventType === et.value ? 'var(--sage-pale)' : 'var(--white)',
                }}
                onClick={() => set('eventType', et.value)}
              >
                <span style={{ fontSize: 24, display: 'block', marginBottom: 6 }}>{et.emoji}</span>
                <div style={{ ...s.etTitle, color: form.eventType === et.value ? 'var(--sage-deep)' : '#2C4A2E' }}>
                  {et.label}
                </div>
                <div style={s.etSub}>{et.sub}</div>
              </button>
            ))}
          </div>
        </Field>

        {/* Price */}
        {form.eventType === 'paid' && (
          <Field label="Price (€) *" error={errors.price}>
            <input
              style={{ ...s.input, borderColor: errors.price ? 'var(--error)' : '#C8D9C9' }}
              type="number"
              value={form.price}
              onChange={e => set('price', e.target.value)}
            />
          </Field>
        )}

        {/* Contact details */}
        <Field label="Your phone number (optional)">
          <input
            style={{ ...s.input, borderColor: '#C8D9C9' }}
            type="tel"
            value={form.organiserPhone}
            onChange={e => set('organiserPhone', e.target.value)}
          />
        </Field>

        <Field label="Your email (optional)">
          <input
            style={{ ...s.input, borderColor: '#C8D9C9' }}
            type="email"
            value={form.organiserEmail}
            onChange={e => set('organiserEmail', e.target.value)}
          />
        </Field>

        <Field label="Booking link (optional)">
          <input
            style={{ ...s.input, borderColor: '#C8D9C9' }}
            type="url"
            value={form.bookingUrl}
            onChange={e => set('bookingUrl', e.target.value)}
          />
        </Field>

        {/* Image */}
        <Field label="Event image (optional)">
          <label style={s.imageUpload}>
            <input
              type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => setImageFile(e.target.files[0])}
            />
            {imageFile
              ? <span style={{ color: 'var(--sage-deep)', fontWeight: 700 }}>✓ {imageFile.name}</span>
              : form.imageUrl
                ? <span style={{ color: '#2C4A2E', fontWeight: 600 }}>✓ Image uploaded — tap to replace</span>
                : <span style={{ color: '#2C4A2E', fontWeight: 600 }}>📷 Tap to add a photo</span>
            }
          </label>
        </Field>

        {/* Repeat */}
        <Field label="Does this event repeat?">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
            {REPEATS.map(r => (
              <button
                key={r.value}
                style={{
                  ...s.tileBtn,
                  border: form.repeat === r.value ? '2px solid var(--sage-deep)' : '1.5px solid #C8D9C9',
                  background: form.repeat === r.value ? 'var(--sage-pale)' : 'var(--white)',
                  color: form.repeat === r.value ? 'var(--sage-deep)' : '#2C4A2E',
                  fontWeight: form.repeat === r.value ? 800 : 600,
                }}
                onClick={() => set('repeat', r.value)}
              >
                <span style={{ fontSize: 18, display: 'block', marginBottom: 4 }}>{r.emoji}</span>
                {r.label}
              </button>
            ))}
          </div>
        </Field>

      </div>

      {/* Save bar */}
      <div style={s.submitBar}>
        <button
          onClick={handleSave}
          style={{ ...s.submitBtn, opacity: saving ? 0.7 : 1 }}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

const Field = ({ label, error, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: '#2C4A2E' }}>
      {label}
    </span>
    {children}
    {error && <span style={{ fontSize: 12, color: 'var(--error)', fontWeight: 600 }}>{error}</span>}
  </div>
);

const s = {
  back: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 12 },
  backCircle: {
    width: 34, height: 34, background: 'var(--white)', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 15, boxShadow: 'var(--shadow-sm)', color: '#2C4A2E',
  },
  backLbl: { fontSize: 13, color: '#2C4A2E', fontWeight: 600 },
  h2: { fontSize: 26, fontWeight: 800, color: '#1A2E1A', letterSpacing: '-0.3px' },

  form: { padding: '0 18px', display: 'flex', flexDirection: 'column', gap: 16 },

  input: {
    width: '100%', padding: '12px 14px',
    borderRadius: 12, border: '1.5px solid #C8D9C9',
    fontSize: 15, fontFamily: 'Nunito, sans-serif',
    color: '#1A2E1A', fontWeight: 500,
    background: 'var(--white)', boxSizing: 'border-box', outline: 'none',
  },

  tileBtn: {
    background: 'var(--white)', border: '1.5px solid #C8D9C9',
    borderRadius: 13, padding: '11px 6px',
    textAlign: 'center', cursor: 'pointer',
    fontSize: 12, fontWeight: 600,
    fontFamily: 'Nunito, sans-serif', transition: 'all 0.15s',
  },

  etypeBtn: {
    background: 'var(--white)', border: '1.5px solid #C8D9C9',
    borderRadius: 14, padding: '16px 12px',
    textAlign: 'center', cursor: 'pointer',
    fontFamily: 'Nunito, sans-serif', transition: 'all 0.15s',
  },
  etTitle: { fontSize: 15, fontWeight: 800, marginBottom: 3 },
  etSub: { fontSize: 11, color: '#4A6A4A', fontWeight: 500 },

  imageUpload: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--white)', border: '1.5px dashed #C8D9C9',
    borderRadius: 14, padding: '20px', cursor: 'pointer',
    fontSize: 14, fontWeight: 600,
  },

  submitBar: {
    position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
    width: '100%', maxWidth: 430,
    padding: '13px 18px 26px',
    background: 'var(--white)', borderTop: '1.5px solid var(--border)', zIndex: 100,
  },
  submitBtn: {
    width: '100%', background: 'var(--sage-deep)', color: 'white',
    border: 'none', padding: 17, borderRadius: 16,
    fontSize: 16, fontWeight: 700, fontFamily: 'Nunito, sans-serif',
    cursor: 'pointer',
  },
};

export default EditEventPage;
